import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import RichTextEditor from '../../../../components/widgets/RichTextEditor';

/**
 * 썸네일2 팝업 컴포넌트
 * 이미지 업로드 + Quill 에디터
 */
const Thumbnail2Popup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [validated, setValidated] = useState(false);
  const formRef = useRef(null);
  const quillEditorRef = useRef(null);

  useEffect(() => {
    if (config?.initialData) {
      setTitle(config.initialData.title || '');
      setDescription(config.initialData.description || '');
      setImage(config.initialData.image || null);
    }
    setValidated(false);
  }, [config?.initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Quill 에디터 데이터 가져오기
    let finalDescription = description;
    if (quillEditorRef.current) {
      finalDescription = quillEditorRef.current.getHTML();
      if (!finalDescription || finalDescription.trim() === '' || finalDescription === '<p><br></p>') {
        const errorSpan = document.getElementById('popup-thumbnail2-editor-span');
        if (errorSpan) {
          errorSpan.style.display = 'block';
        }
        setValidated(true);
        return;
      }
    }

    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setValidated(true);
      return;
    }

    // 에디터 데이터가 비어있으면 에러 표시
    if (!finalDescription || finalDescription.trim() === '' || finalDescription === '<p><br></p>') {
      const errorSpan = document.getElementById('popup-thumbnail2-editor-span');
      if (errorSpan) {
        errorSpan.style.display = 'block';
      }
      setValidated(true);
      return;
    }

    onClose(true, { image, title, description: finalDescription });
  };

  const handleLeftButtonClick = () => {
    onClose(false);
  };

  const handleRightButtonClick = () => {
    // Quill 에디터 데이터 가져오기
    if (quillEditorRef.current) {
      const editorData = quillEditorRef.current.getHTML();
      setDescription(editorData);

      // 에디터 데이터가 비어있으면 에러 표시
      if (!editorData || editorData.trim() === '' || editorData === '<p><br></p>') {
        const errorSpan = document.getElementById('popup-thumbnail2-editor-span');
        if (errorSpan) {
          errorSpan.style.display = 'block';
        }
        setValidated(true);
        return;
      } else {
        const errorSpan = document.getElementById('popup-thumbnail2-editor-span');
        if (errorSpan) {
          errorSpan.style.display = 'none';
        }
      }
    }

    const form = formRef.current;
    if (form) {
      form.requestSubmit();
    }
  };

  return (
    <PopupWrapper
      config={config}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-uploader"
    >
      <div className="main-popup-body">
        <div className="main-popup-uploader">
          <div className="row" style={{ marginBottom: '20px' }}>
            <div className="col-md-8 col-12">
              <label>
                {t('popup.thumbnail2Popup.coverImage')}<span style={{ color: 'red' }}>*</span>
              </label>
              <div>
                <div id="popup-thumbnail2-uploader" style={{ display: 'none' }} className="mt-1"></div>
                <div id="popup-thumbnail2-img-show" style={{ display: 'block' }} className="mt-1">
                  <img
                    src={image?.url || '/assets/images/page/register/profile_empty.png'}
                    alt="thumbnail2"
                    id="popup-thumbnail2-img"
                    data-id={image?.fileId || ''}
                    style={{ width: '200px' }}
                  />
                </div>
                <div className="info_text">
                  <p style={{ fontSize: '12px !important', marginBottom: '3px !important' }}>
                    {t('popup.thumbnail2Popup.imageFormat')}
                  </p>
                  <p
                    style={{ fontSize: '12px !important', color: 'red', marginBottom: '3px !important' }}
                    id="popup-thumbnail2-uploader-image-alert-span"
                  ></p>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'left', gap: '20px' }}>
                <button
                  type="button"
                  className="btn btn-dark"
                  style={{
                    height: '35px',
                    width: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  id="popup-thumbnail2-modal-image-upload-btn"
                >
                  {t('popup.thumbnail2Popup.uploadFile')}
                </button>
                <button
                  type="button"
                  className="btn btn-dark"
                  style={{
                    height: '35px',
                    width: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  id="popup-thumbnail2-modal-image-delete-btn"
                >
                  {t('popup.thumbnail2Popup.deleteFile')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="main-popup-form">
          <form
            ref={formRef}
            className={`needs-validation ${validated ? 'was-validated' : ''}`}
            noValidate
            onSubmit={handleSubmit}
          >
            <div className="row">
              <div className="single-input-item">
                <label htmlFor="popup-thumbnail2-input" className="required">
                  {t('popup.thumbnail2Popup.workTitle')}<span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  className="form-control"
                  type="text"
                  id="popup-thumbnail2-input"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <div className="invalid-feedback">{t('popup.thumbnail2Popup.requiredInfo')}</div>
              </div>
              <div className="main-popup-textarea">
                <div className="single-input-item">
                  <label>
                    {t('popup.thumbnail2Popup.workDescription')}<span style={{ color: 'red' }}>*</span>
                  </label>
                  <div style={{ marginBottom: '10px' }}>
                    <RichTextEditor
                      ref={quillEditorRef}
                      data={{
                        id: 'popup-thumbnail2-quill-editor',
                        initialContent: description,
                        content: description, // 외부에서 내용 변경 시 사용
                        quillOptions: {
                          theme: 'snow',
                          modules: {
                            toolbar: [
                              [{ 'header': [1, 2, 3, false] }],
                              ['bold', 'italic', 'underline', 'strike'],
                              ['link', 'image'],
                              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                              [{ 'color': [] }, { 'background': [] }],
                              ['clean']
                            ]
                          },
                          formats: [
                            'header',
                            'bold', 'italic', 'underline', 'strike',
                            'link', 'image',
                            'list', 'bullet',
                            'color', 'background'
                          ],
                          placeholder: t('popup.thumbnail2Popup.editorPlaceholder')
                        }
                      }}
                      onChange={(htmlContent) => {
                        setDescription(htmlContent);
                      }}
                    />
                  </div>
                  <div id="popup-thumbnail2-editor-span" style={{ display: 'none', marginBottom: '20px' }}>
                    <span style={{ fontSize: '14px', color: 'orangered' }}>{t('popup.thumbnail2Popup.requiredInfo')}</span>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div className="main-popup-footer d-flex justify-content-between">
        {config?.leftButton && (
          <button
            type="button"
            className={`btn main-popup-left-btn ${getButtonClass(config.leftButton.color || 'btn-secondary')}`}
            onClick={handleLeftButtonClick}
          >
            {config.leftButton.text || t('popup.common.cancel')}
          </button>
        )}
        {config?.rightButton && (
          <button
            type="button"
            className={`btn main-popup-right-btn ${getButtonClass(config.rightButton.color || 'btn-primary')}`}
            onClick={handleRightButtonClick}
          >
            {config.rightButton.text || t('popup.common.save')}
          </button>
        )}
      </div>
    </PopupWrapper>
  );
};

export default Thumbnail2Popup;

